import React, { useState, useEffect } from 'react';
import { useAuth, api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Users, Gift, Copy, Check, Share2, TrendingUp, Loader2, Crown, Calendar } from 'lucide-react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

const Referrals = () => {
  const { currentUser } = useAuth();
  
  // State
  const [referralCode, setReferralCode] = useState('');
  const [shareUrl, setShareUrl] = useState('');
  const [stats, setStats] = useState(null);
  const [referredUsers, setReferredUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  // Fetch referral data
  useEffect(() => {
    const fetchReferralData = async () => {
      if (!currentUser) return;
      
      try {
        setLoading(true);
        
        // Get referral code
        const codeRes = await api.get('/api/referrals/my-code');
        setReferralCode(codeRes.data.referral_code);
        setShareUrl(codeRes.data.share_url);
        
        // Get stats
        const statsRes = await api.get('/api/referrals/stats');
        setStats(statsRes.data);
        
        // Get referred users
        const usersRes = await api.get('/api/referrals/my-referrals');
        setReferredUsers(usersRes.data);
        
        setLoading(false);
      } catch (error) {
        console.error('Error fetching referral data:', error);
        toast.error('Failed to load referral data');
        setLoading(false);
      }
    };
    
    fetchReferralData();
  }, [currentUser]);

  const copyReferralLink = () => {
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const shareReferral = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Join Sauce Sity Barbershop',
        text: `Get premium cuts at Sauce Sity! Use my referral code: ${referralCode}`,
        url: shareUrl
      }).catch(err => console.log('Share failed:', err));
    } else {
      copyReferralLink();
    }
  };

  if (!currentUser) {
    return (
      <div className="sauce-sity-page" data-testid="referrals-page-guest">
        <div className="sauce-sity-content text-center" style={{ paddingTop: 80 }}>
          <Users className="w-20 h-20 mx-auto mb-6 text-lime-400" />
          <h1 className="ss-title-xl mb-4">Referral <span className="ss-highlight-green">Program</span></h1>
          <p className="text-zinc-400 mb-8">Sign in to access your referral rewards</p>
          <Link to="/login">
            <button className="ss-btn ss-btn-primary ss-btn-lg" data-testid="referrals-signin-btn">
              Sign In
            </button>
          </Link>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="sauce-sity-page flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-lime-400 animate-spin" />
      </div>
    );
  }

  return (
    <div className="sauce-sity-page" data-testid="referrals-page">
      <div className="sauce-sity-content" style={{ maxWidth: 1200 }}>
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="ss-title-xl mb-4">
            <span className="ss-highlight-green">Refer</span> & Earn
          </h1>
          <p className="text-lg text-zinc-400">Share the Sauce, earn rewards</p>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
          <Card className="bg-gradient-to-br from-lime-500/10 to-lime-600/10 border-lime-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-zinc-400 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Total Referrals
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-lime-400">{stats?.total_referrals || 0}</p>
              <p className="text-xs text-zinc-500 mt-1">Friends referred</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 border-purple-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-zinc-400 flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                Bookings
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-purple-400">{stats?.successful_bookings || 0}</p>
              <p className="text-xs text-zinc-500 mt-1">First appointments</p>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-yellow-500/10 to-yellow-600/10 border-yellow-500/30">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm text-zinc-400 flex items-center">
                <Crown className="w-4 h-4 mr-2" />
                Memberships
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-4xl font-bold text-yellow-400">{stats?.successful_memberships || 0}</p>
              <p className="text-xs text-zinc-500 mt-1">Friends joined</p>
            </CardContent>
          </Card>
        </div>

        {/* Share Your Code */}
        <Card className="bg-zinc-900 border-zinc-800 mb-12">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Gift className="w-5 h-5 mr-2 text-lime-400" />
              Your Referral Code
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <label className="block text-sm text-zinc-400 mb-2">Referral Code</label>
              <div className="flex space-x-3">
                <Input
                  value={referralCode}
                  readOnly
                  className="bg-zinc-800 border-zinc-700 text-white font-mono text-2xl text-center"
                />
                <Button
                  onClick={copyReferralLink}
                  className="bg-zinc-800 hover:bg-zinc-700 border border-zinc-700"
                >
                  {copied ? (
                    <Check className="w-5 h-5 text-lime-400" />
                  ) : (
                    <Copy className="w-5 h-5" />
                  )}
                </Button>
              </div>
            </div>

            <div>
              <label className="block text-sm text-zinc-400 mb-2">Share Link</label>
              <div className="flex space-x-3">
                <Input
                  value={shareUrl}
                  readOnly
                  className="bg-zinc-800 border-zinc-700 text-white text-sm"
                />
                <Button
                  onClick={shareReferral}
                  className="bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700 font-semibold"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* How It Works */}
        <Card className="bg-zinc-900 border-zinc-800 mb-12">
          <CardHeader>
            <CardTitle className="flex items-center">
              <TrendingUp className="w-5 h-5 mr-2 text-purple-400" />
              How Referral Rewards Work
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <div className="bg-lime-500/10 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">✂️</span>
                </div>
                <h3 className="font-bold text-lg mb-2">First Booking</h3>
                <p className="text-zinc-400 text-sm mb-2">When your friend books their first haircut</p>
                <Badge className="bg-lime-500/20 text-lime-400">10% Off + 50 Points</Badge>
              </div>

              <div>
                <div className="bg-purple-500/10 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">👑</span>
                </div>
                <h3 className="font-bold text-lg mb-2">Membership Signup</h3>
                <p className="text-zinc-400 text-sm mb-2">When your friend joins a membership</p>
                <Badge className="bg-purple-500/20 text-purple-400">25% Off + 100 Points</Badge>
              </div>

              <div>
                <div className="bg-yellow-500/10 w-16 h-16 rounded-full flex items-center justify-center mb-4">
                  <span className="text-3xl">🎉</span>
                </div>
                <h3 className="font-bold text-lg mb-2">Milestone Reward</h3>
                <p className="text-zinc-400 text-sm mb-2">Refer 3 friends to membership</p>
                <Badge className="bg-yellow-500/20 text-yellow-400">FREE Haircut</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Referred Friends */}
        {referredUsers.length > 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardHeader>
              <CardTitle>Your Referrals ({referredUsers.length})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {referredUsers.map((user) => (
                  <div key={user.id} className="flex items-center justify-between border-b border-zinc-800 pb-4 last:border-0">
                    <div className="flex items-center space-x-4">
                      <div className="w-12 h-12 rounded-full bg-gradient-to-r from-lime-500 to-lime-600 flex items-center justify-center">
                        <span className="text-black font-bold text-lg">
                          {user.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div>
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-sm text-zinc-500">{user.email}</p>
                        <p className="text-xs text-zinc-600">
                          Joined {new Date(user.referred_date).toLocaleDateString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            year: 'numeric'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col items-end space-y-2">
                      {user.has_membership && (
                        <Badge className="bg-gradient-to-r from-lime-500 to-lime-600 text-black">
                          <Crown className="w-3 h-3 mr-1" />
                          Member
                        </Badge>
                      )}
                      {user.has_booked && (
                        <Badge className="bg-purple-500/20 text-purple-400">
                          <Check className="w-3 h-3 mr-1" />
                          Booked
                        </Badge>
                      )}
                      {!user.has_booked && !user.has_membership && (
                        <Badge className="bg-zinc-700 text-zinc-400">Pending</Badge>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Empty State */}
        {referredUsers.length === 0 && (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="text-center py-12">
              <Users className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-bold mb-2">No Referrals Yet</h3>
              <p className="text-zinc-400 mb-6">
                Share your referral code and start earning rewards!
              </p>
              <Button
                onClick={shareReferral}
                className="bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700 font-semibold"
              >
                <Share2 className="w-4 h-4 mr-2" />
                Share Your Code
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Referrals;
